import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { PitchCard } from "@/components/PitchCard.tsx"
import { useQuery } from "@tanstack/react-query"
import { pitchesApi } from "@/api/pitches.api.ts"
import { Card, CardContent, CardHeader } from "@/components/ui/card.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item.tsx"
import { CreatePitchButton } from "@/components/CreatePitchButton.tsx"
import { FaceNeutral } from "lucide-react"
import { ErrorCard } from "@/components/ErrorCard.tsx"
import { AppPagination } from "@/components/AppPagination.tsx"
import { useTranslation } from "react-i18next"

export const Route = createFileRoute("/_protected/commissions")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Number(search.page ?? 1),
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const [pitches, setPitches] = useState([])
  const { page } = useSearch({ from: "/_protected/commissions" })
  const skipCount = (page - 1) * 10
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["pitches/getCommissions", skipCount],
    queryFn: () => pitchesApi.getCommissions({ skipCount }),
    structuralSharing: false,
  })

  useEffect(() => {
    setTitle(t("sidebar.commissions"))
    if (data?.result?.totalCount > 0) {
      setTitle(`${t("sidebar.commissions")} (${data?.result?.totalCount})`)
    }
  }, [setTitle, data])

  useEffect(() => {
    if (data && data.result) {
      setPitches(data.result.items)
    }
  }, [data, page])

  if (isLoading || isFetching) {
    return (
      <div className="flex w-full flex-wrap justify-between pb-3">
        <div className="flex-[100%] !cursor-pointer p-3 pr-0 pl-0 md:flex-1/2 md:nth-of-type-[2n]:pl-3 md:nth-of-type-[2n+1]:pr-3">
          <Card className="w-full">
            <CardHeader>
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </CardContent>
          </Card>
        </div>
        <div className="flex-[100%] !cursor-pointer p-3 pr-0 pl-0 md:flex-1/2 md:nth-of-type-[2n]:pl-3 md:nth-of-type-[2n+1]:pr-3">
          <Card className="w-full">
            <CardHeader>
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (error) {
    return <ErrorCard isRefresh />
  }

  return (
    <div className="flex w-full flex-wrap justify-start pb-6">
      {pitches.length === 0 && (
        <Item
          variant="muted"
          className="flex flex-col items-center justify-center gap-3"
        >
          <ItemContent className="mt-8 flex items-center gap-4 text-center">
            <FaceNeutral size={48} className="text-secondary" />
            <ItemTitle className="font-heading text-xl">
              Sorry! You don't have any commissions yet!
            </ItemTitle>
            <ItemDescription>Create pitches to get commissions</ItemDescription>
          </ItemContent>
          <ItemActions className="m-8">
            <CreatePitchButton />
          </ItemActions>
        </Item>
      )}
      {pitches.length > 0 &&
        pitches.map((pitch) => (
          <div
            key={pitch.publicId}
            className="w-full !cursor-pointer p-3 ps-0 pe-0 md:flex-[0_0_50%] md:[&:nth-of-type(2n)]:pe-3 md:[&:nth-of-type(2n+1)]:ps-3"
          >
            <PitchCard pitch={pitch} />
          </div>
        ))}
      <div className="flex w-full justify-end">
        <AppPagination
          currentPage={page}
          totalCount={data?.result?.totalCount ?? 0}
          onPageChange={(page) => {
            navigate({
              search: (prev) => ({
                ...prev,
                page,
              }),
            })
          }}
        />
      </div>
    </div>
  )
}
